import type { BackendMessage } from '../../state/types'
import { authFetch, buildAgentUrl, throwApiError, unwrapArray, unwrapObject } from './core'
import { requestOptions } from './workspace'

export interface BackendChat {
  id: string
  title: string
  userId: string
  classId?: string | null
  classTitle?: string | null
  messageCount: number
  summary?: string
  createdAt: string
  updatedAt: string
}

export type { BackendMessage } from '../../state/types'

export interface FetchMessagesResult {
  messages: BackendMessage[]
  hasMore: boolean
}

export function normalizeChatPayload(json: unknown): BackendChat {
  return unwrapObject<BackendChat>(json, ['chat', 'data', 'item'])
}

export async function fetchChats(token: string): Promise<BackendChat[]> {
  const response = await authFetch(buildAgentUrl('/chats'), requestOptions(token))
  if (!response.ok) {
    await throwApiError(response, 'Failed to fetch chats')
  }

  const json = await response.json()
  const chats = unwrapArray<BackendChat>(json, ['chats', 'data'])
  if (chats.length > 0 || Array.isArray(json)) return chats

  console.warn('fetchChats: unexpected response shape', json)
  return []
}

export async function fetchMessages(
  chatId: string,
  token: string,
  options?: { before?: string; limit?: number },
): Promise<FetchMessagesResult> {
  const url = new URL(buildAgentUrl(`/chats/${chatId}/messages`))
  if (options?.before) url.searchParams.set('before', options.before)

  const limit = options?.limit ?? 10
  url.searchParams.set('limit', String(limit))

  const response = await authFetch(url.toString(), requestOptions(token))
  if (!response.ok) {
    await throwApiError(response, 'Failed to fetch messages')
  }

  const json = await response.json()
  const messages = unwrapArray<BackendMessage>(json, ['messages', 'data'])
  const hasMore = typeof json?.hasMore === 'boolean' ? json.hasMore : messages.length >= limit

  return { messages, hasMore }
}

export async function createChat(token: string): Promise<{ chatId: string; chat: BackendChat }> {
  const response = await authFetch(buildAgentUrl('/robolabx/chat'), {
    method: 'POST',
    ...requestOptions(token, { 'Content-Type': 'application/json' }),
  })
  if (!response.ok) {
    await throwApiError(response, 'Failed to create chat')
  }
  return response.json() as Promise<{ chatId: string; chat: BackendChat }>
}

export async function createClassChat(
  token: string,
  classId: string,
): Promise<{ chatId: string; chat: BackendChat }> {
  const response = await authFetch(buildAgentUrl(`/classes/${classId}/chats`), {
    method: 'POST',
    ...requestOptions(token, { 'Content-Type': 'application/json' }),
  })
  if (!response.ok) {
    await throwApiError(response, 'Failed to create class conversation')
  }

  const json = await response.json()
  const chat = normalizeChatPayload(json)
  return { chatId: chat.id, chat: { ...chat, classId } }
}

export async function deleteChat(chatId: string, token: string): Promise<void> {
  const response = await authFetch(buildAgentUrl(`/chats/${chatId}`), {
    method: 'DELETE',
    ...requestOptions(token),
  })
  if (!response.ok) {
    await throwApiError(response, 'Failed to delete chat')
  }
}
